"use client";
import { useEffect, useMemo, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import CTALink from "@/components/CTA/ctaLink";
import ProductCardList from "@/components/ProductCardList";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
  FormatSubType,
} from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { ElementClicked } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementClicked";
import { cn } from "@/lib/utils";
import { makeSafeAttributeString } from "@/utils/functions/getIdLabel";
import { TagularVariant } from "@/utils/tagular/dataTranslationMatrices";
import type { ProductCardListByUUIDClientProps } from "./types";
import { hyphenateForTagular } from "@/utils/tagular/helpers";
import { personalizeProductList } from "@/lib/productSummary/helper/personalizeProductList";
import Message from "@/components/ContentBlock/variants/Message";
import MonarchResolver from "@/monarch/MonarchResolver";
import { HubCardReorderTestResult } from "@/monarch/rules/hubCardReorder/types";
import { UmbrellaPromoPageTestResult } from "@/monarch/rules/umbrellaPromoPage/types";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import ProductCardSkeleton from "@/components/ui/skeleton/variants/productCardSkeleton";
import useTranslations from "@/i18n/useTranslations";

export default function ProductCardListByUUIDClient({
  id = "",
  title = "",
  cardFormatSubtype = FormatSubType.Grid,
  cardWrapperClassName = "",
  contractText = "",
  ctaLabel = "",
  ctaUrl = "",
  ctaRadius = "none",
  ctaBackgroundColor,
  className = "",
  container_class,
  expandText = "",
  initialCardsLength = 4,
  isProfEdPage = false,
  isMultiCourse = false,
  location = "",
  products,
  productType = "",
  showAllProducts = false,
  fallbackProducts = [],
  copy,
  showDisclaimer,
  children,
  isEdAssist = false,
  enableMonarch = false,
}: ProductCardListByUUIDClientProps) {
  const tExpand = useTranslations("ExpandableBlock");
  const [isExpanded, setIsExpanded] = useState(false);

  const expandLabel = expandText || tExpand("showMore");
  const contractLabel = contractText || tExpand("showLess");

  const tagular_variant = isProfEdPage
    ? TagularVariant.ProfessionalCertificate
    : undefined;

  const webElement: ElementClicked["webElement"] = {
    location,
    position: "1",
    elementType: ElementType.Link,
    htmlId: "show-more-link",
    text: isExpanded ? "Show Less" : "Show More",
    name: isExpanded
      ? hyphenateForTagular("Show Less")
      : hyphenateForTagular("Show More"),
  };

  const { elementClicked, elementViewed } = useElementEvents({
    webElement,
  });
  const { ref: showMoreRef, inView: showMoreInView } = useInView(
    DEFAULT_USE_IN_VIEW_OPTIONS,
  );

  useEffect(() => {
    if (showMoreInView) {
      elementViewed();
    }
  }, [showMoreInView, elementViewed]);

  const isUsingFallback = personalizeProductList(products).length === 0;

  const filteredProducts = isUsingFallback
    ? fallbackProducts
    : personalizeProductList(products);

  const initialProducts = useMemo(
    () => filteredProducts?.slice(0, initialCardsLength),
    [filteredProducts, initialCardsLength],
  );

  // Only return null if Monarch is NOT enabled and there are no products
  if (!enableMonarch && (!filteredProducts || filteredProducts.length === 0)) {
    return null;
  }

  const titleId = makeSafeAttributeString(title);

  const displayedProducts =
    isExpanded || showAllProducts ? filteredProducts : initialProducts;
  const skipEvents = isExpanded ? initialProducts : [];

  const handleShowButtonClick = () => {
    elementClicked();
    setIsExpanded(!isExpanded);
  };

  const ctaLink = (currentLabel?: string, currentUrl?: string) => (
    <CTALink
      html_id="subject-product-link"
      elementType={ElementType.Entry}
      location={location}
      variant={webElement.variant}
      url={(currentUrl || ctaUrl) as string}
      label={(currentLabel || ctaLabel) as string}
      radius={ctaRadius}
      size="lg"
      styles={
        ctaBackgroundColor
          ? `--cta-bg-color: ${ctaBackgroundColor};`
          : undefined
      }
    />
  );

  /**
   * Skeleton loader component that shows loading animation for ProductCardList
   */
  const ProductCardListSkeleton = () => {
    return (
      <div className="md:mb-12">
        {/* Title skeleton */}
        <div className="h-8 bg-gray-200 rounded w-1/3 mb-6 animate-pulse"></div>

        {/* Grid of product card skeletons - matches ProductCardList structure */}
        <div className="dynamic-grid flex-wrap gap-4 py-4">
          {Array.from({ length: initialCardsLength }).map((_, i) => (
            <ProductCardSkeleton key={i} />
          ))}
        </div>

        {/* CTA skeleton */}
        <div className="flex justify-center mt-6 md:mt-5">
          <div className="h-10 bg-gray-200 rounded w-32 animate-pulse"></div>
        </div>
      </div>
    );
  };

  const shouldShowTitle = title || isUsingFallback;
  const shouldShowDisclaimer = showDisclaimer && isUsingFallback;

  // Render function that can use Monarch data or default props
  const renderContent = (
    currentTitle: string,
    currentCtaLabel: string,
    currentCtaUrl: string,
    currentProducts: typeof filteredProducts,
    isLoading = false,
  ) => {
    const currentDisplayedProducts =
      isExpanded || showAllProducts
        ? currentProducts
        : currentProducts.slice(0, initialCardsLength);

    const currentIsEmptyList = currentDisplayedProducts.length === 0;

    // Calculate shouldShowCTA here using currentProducts instead
    const shouldShowCTA =
      !showAllProducts && (currentProducts.length ?? 0) > initialCardsLength;

    // Don't render if there are no products to show and not loading
    if (currentIsEmptyList && !isLoading) {
      return null;
    }

    return (
      <>
        {shouldShowDisclaimer && <Message variant="no-degree" copy={copy} />}
        {children && children}
        <div className={cn("md:mb-12", cardWrapperClassName)}>
          {(shouldShowTitle || currentTitle) && (
            <h4 id={titleId || undefined} className="-mb-1">
              <span className="inline-flex gap-2 items-center justify-center break-words whitespace-normal">
                {isUsingFallback
                  ? copy
                    ? `Explore related ${copy} programs and courses`
                    : `Explore related programs and courses`
                  : currentTitle}
              </span>
            </h4>
          )}

          <MonarchResolver<HubCardReorderTestResult>
            experimentKey="useHubCardReorder"
            variants={{
              control: (
                <ProductCardList
                  products={currentDisplayedProducts}
                  shouldScroll={false}
                  location={location}
                  cardFormatSubType={cardFormatSubtype}
                  className={className}
                  skipViewEvents={skipEvents}
                  isMultiCourse={isMultiCourse}
                  tagular_variant={tagular_variant}
                  container_class={container_class}
                  isEdAssist={isEdAssist}
                />
              ),
              reordered: (
                <MonarchDataConsumer<HubCardReorderTestResult>
                  experimentKey="useHubCardReorder"
                  render={({ data }) => {
                    const section = data?.masters?.find(
                      (section) =>
                        (currentCtaUrl && section.ctaUrl === currentCtaUrl) ||
                        (copy && section.copy === copy),
                    );

                    const orderedUuids = section?.uuids || [];

                    const productsByUuid = new Map(
                      currentProducts.map((product) => [
                        product.productUuid,
                        product,
                      ]),
                    );

                    const reorderedProducts = orderedUuids
                      .map((uuid) => productsByUuid.get(uuid))
                      .filter(
                        (product): product is NonNullable<typeof product> =>
                          product !== undefined,
                      );

                    const remainingProducts = currentProducts.filter(
                      (product) => !orderedUuids.includes(product.productUuid),
                    );

                    const productsInOrder = [
                      ...reorderedProducts,
                      ...remainingProducts,
                    ];

                    const finalProducts =
                      isExpanded || showAllProducts
                        ? productsInOrder
                        : productsInOrder.slice(0, initialCardsLength);

                    return (
                      <ProductCardList
                        products={finalProducts}
                        shouldScroll={false}
                        location={location}
                        cardFormatSubType={cardFormatSubtype}
                        className={className}
                        skipViewEvents={skipEvents}
                        isMultiCourse={isMultiCourse}
                        tagular_variant={tagular_variant}
                        container_class={container_class}
                        isEdAssist={isEdAssist}
                      />
                    );
                  }}
                />
              ),
            }}
            fallbackVariant="control"
          />

          <div className="flex justify-center mt-6 md:mt-5">
            {shouldShowCTA ? (
              isExpanded && currentCtaUrl && currentCtaLabel ? (
                ctaLink(currentCtaLabel, currentCtaUrl)
              ) : (
                <span
                  role="button"
                  ref={showMoreRef}
                  className="underline cursor-pointer"
                  onClick={handleShowButtonClick}
                >
                  {isExpanded ? contractLabel : expandLabel}
                </span>
              )
            ) : (
              currentCtaUrl &&
              currentCtaLabel &&
              ctaLink(currentCtaLabel, currentCtaUrl)
            )}
          </div>
        </div>
      </>
    );
  };

  // If Monarch is NOT enabled, render normally
  if (!enableMonarch) {
    return renderContent(title, ctaLabel, ctaUrl, filteredProducts, false);
  }

  const renderWithMonarchData = (
    testResult: UmbrellaPromoPageTestResult | null | undefined,
  ) => {
    // Check if still loading
    if (testResult?.isLoading) {
      return renderContent(
        title,
        ctaLabel,
        ctaUrl,
        filteredProducts.length > 0 ? filteredProducts : [],
        true,
      );
    }

    // Start with shortcode values
    let finalCtaLabel = ctaLabel;
    let finalCtaUrl = ctaUrl;
    let finalProducts = filteredProducts;

    // Match by id using the id prop from the shortcode
    const testData = id
      ? testResult?.data?.productLists?.find((pl) => pl.id === id)
      : undefined;

    if (testData) {
      // Override CTA if provided
      if (testData.cta?.text) {
        finalCtaLabel = testData.cta.text;
      }
      if (testData.cta?.url) {
        finalCtaUrl = testData.cta.url;
      }

      // Use hydrated products from Monarch if available
      if (testData.products && testData.products.length > 0) {
        finalProducts = personalizeProductList(testData.products);
      }
    }

    return renderContent(
      title,
      finalCtaLabel,
      finalCtaUrl,
      finalProducts,
      false,
    );
  };

  // If Monarch IS enabled, wrap with Umbrella Promo resolver - SAME PATTERN AS PROMOBANNER
  const monarchProductListComponent = (
    <MonarchDataConsumer<UmbrellaPromoPageTestResult>
      experimentKey="useUmbrellaPromoPage"
      render={(testResult) => renderWithMonarchData(testResult || null)}
    />
  );

  return (
    <MonarchResolver<UmbrellaPromoPageTestResult>
      experimentKey="useUmbrellaPromoPage"
      Loader={<ProductCardListSkeleton />}
      variants={{
        control: monarchProductListComponent,
        test: monarchProductListComponent,
      }}
      fallbackVariant="control"
    />
  );
}
